/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import get from 'lodash/get';
// module
import FetchService from 'modules/Fetch';
import AnalyticsService from 'modules/Analytics';
import UserService from 'modules/User';
import LocaleService from 'modules/Localization';
import AppService from 'modules/App/service';
// utils
import { broker } from 'modules/TradingChart/consts';
import { getStringSizeInKb } from 'modules/TradingChart/components/utils/ChartSizeCalculator';
// constants
import { EVENTS } from 'modules/Analytics/consts';
import { NotificationType } from '../TradingChart/components/TradingView/sources/charting_library';
// typings
import {
  ChartSettingsResponse,
  ChartSettingsRequest,
  SettingsType,
  SaveChartSettingsRequest,
  ChartSettingsListResponse,
  SaveChartSettingsResponse,
  ChartLayoutResponse,
} from './typings';

class SettingsService {
  getIsChartSettingsSaveEnabled = () =>
    UserService.userPreferenceSettings?.saveChartsToServer;

  getIsFirstTimeUser = () =>
    UserService.userPreferenceSettings?.saveChartsToServer === undefined;

  getIsAutoSaveChartsEnabled = () =>
    UserService.userPreferenceSettings?.autoSaveCharts ?? true;

  getChartSettingsContent = async ({
    id,
    name,
    settingsType,
    loadingKey,
  }: ChartSettingsRequest) => {
    try {
      const response = await FetchService.request<
        'USER_SETTINGS',
        ChartSettingsResponse
      >(
        'USER_SETTINGS',
        '/user/chartsettings',
        loadingKey || 'CHART_SETTING_DATA_LOADING',
        {
          headers: {
            ...(id && { id }),
            ...(name && { name }),
            settingsType,
          },
        },
      );

      if (response.success) {
        return response.data as ChartLayoutResponse;
      }
      throw new Error(response?.error?.message);
    } catch (error: any) {
      const errorMessage = error?.message || LocaleService.translate('FETCHING_CHART_SETTINGS_FAILED');

      AnalyticsService.sendEvent(EVENTS.CHART_SETTINGS_GET_ERROR, {
        settingsType,
        errorMessage,
      });

      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('FAILED_TO_LOAD'),
          errorMessage,
          NotificationType.Error,
        );
      }

      return Promise.reject(error);
    }
  };

  getChartSettingsList = async (settingsType: SettingsType) => {
    try {
      const response = await FetchService.request<
        'USER_SETTINGS',
        ChartSettingsListResponse
      >(
        'USER_SETTINGS',
        '/user/chartsettings/list',
        'CHART_SETTING_LIST_DATA_LOADING',
        {
          headers: { settingsType },
        },
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response?.error?.message);
    } catch (error: any) {
      const errorMessage = error?.message || LocaleService.translate('FETCHING_CHART_SETTINGS_FAILED');

      AnalyticsService.sendEvent(EVENTS.CHART_SETTINGS_LIST_GET_ERROR, {
        settingsType,
        errorMessage,
      });

      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('FAILED_TO_LOAD'),
          errorMessage,
          NotificationType.Error,
        );
      }

      return Promise.reject(error);
    }
  };

  deleteChartSettings = async ({
    id,
    name,
    settingsType,
  }: ChartSettingsRequest) => {
    try {
      const response = await FetchService.request<'USER_SETTINGS'>(
        'USER_SETTINGS',
        '/user/chartsettings',
        'CHART_DELETE_SETTING_LOADING',
        {
          headers: {
            ...(id && { id }),
            ...(name && { name }),
            settingsType,
          },
          method: 'DELETE',
        },
      );

      if (!response.success) {
        throw new Error(response?.error?.message);
      }
    } catch (error: any) {
      const errorMessage = error?.message || LocaleService.translate('DELETING_CHART_SETTINGS_FAILED');

      AnalyticsService.sendEvent(EVENTS.CHART_SETTINGS_DELETE_ERROR, {
        settingsType,
        errorMessage,
        id,
      });

      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('FAILED_TO_DELETE'),
          errorMessage,
          NotificationType.Error,
        );
      }

      if (settingsType === SettingsType.SAVING_CHART_LAYOUT) {
        return Promise.reject(error);
      }
    }
  };

  saveChartSettings = async ({
    id,
    settingsType,
    data,
    loadingKey,
  }: SaveChartSettingsRequest) => {
    try {
      const chartContentSizeInKb = getStringSizeInKb(data?.content || '');
      const chartContentSizeLimitInKb =
        AppService.config.chart_content_size_limit_in_kb;

      if (chartContentSizeInKb < chartContentSizeLimitInKb) {
        const response = await FetchService.request<
          'USER_SETTINGS',
          SaveChartSettingsResponse
        >(
          'USER_SETTINGS',
          '/user/chartsettings',
          loadingKey || 'CHART_SAVE_SETTING_LOADING',
          {
            headers: {
              ...(id && { id }),
              settingsType,
              'Content-Type': 'application/json',
            },
            method: 'POST',
            body: { data },
          },
        );
        AnalyticsService.sendEvent(EVENTS.CHART_SIZE, {
          chartContentSize: chartContentSizeInKb,
          id,
          name: get(data, 'name', ''),
        });

        if (response.success) {
          return response?.data?.id;
        }

        if (response instanceof DOMException) {
          AnalyticsService.sendEvent(EVENTS.CHART_SAVE_REQUEST_CANCELLED, {
            settingsType,
            reason: response?.message,
            id,
            name: get(data, 'name', ''),
          });

          return '';
        }

        throw new Error(response?.error?.message);
      } else {
        AnalyticsService.sendEvent(EVENTS.CHART_SIZE_EXCEEDED, {
          chartContentSize: chartContentSizeInKb,
          id,
          name: get(data, 'name', ''),
        });
        throw new Error('Chart Size Exceeded');
      }
    } catch (error: any) {
      const errorMessage = error?.message || LocaleService.translate('SAVING_CHART_SETTINGS_FAILED');

      AnalyticsService.sendEvent(EVENTS.CHART_SETTINGS_SAVE_ERROR, {
        settingsType,
        errorMessage,
        id,
        name: get(data, 'name', ''),
      });

      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('FAILED_TO_SAVE'),
          errorMessage,
          NotificationType.Error,
        );
      }

      return Promise.reject(error);
    }
  };
}

export default new SettingsService();
